import React from 'react';
import Icon from '../../../components/AppIcon';

const Divider = () => <div className="w-px h-4 bg-[#E5E7EB] flex-shrink-0" />;

const Pill = ({ icon, label, value, color }) => (
  <div className="flex items-center gap-1.5 flex-shrink-0">
    <Icon name={icon} size={13} style={{ color }} />
    <span className="text-xs text-[#64748B]">{label}</span>
    <span className="text-xs font-semibold text-[#0F172A]">{value}</span>
  </div>
);

const AutomationOverview = ({ wpStats, pinStats, fbStats, etsyStats, jobStats, lastRefresh, loading }) => {
  const pending = jobStats?.stats?.pending || 0;
  const processing = jobStats?.stats?.processing || 0;
  const generating = jobStats?.stats?.generating || 0;
  const failed = jobStats?.stats?.failed || 0;
  const activeJobs = pending + processing + generating;

  const systemOk = failed === 0 && activeJobs >= 0;
  const systemWarning = failed > 0 && failed < 5;
  const systemCritical = failed >= 5;

  const statusDot = systemCritical
    ? 'bg-red-500'
    : systemWarning
    ? 'bg-amber-500'
    : 'bg-emerald-500';

  const statusLabel = systemCritical ? 'Issues Detected' : systemWarning ? 'Warning' : 'Healthy';
  const statusColor = systemCritical ? 'text-red-600' : systemWarning ? 'text-amber-600' : 'text-emerald-600';

  const fmt = (n) => (typeof n === 'number' ? n.toLocaleString() : '0');

  const relTime = (ts) => {
    if (!ts) return 'Never';
    const s = Math.floor((Date.now() - new Date(ts)) / 1000);
    if (s < 60) return `${s}s ago`;
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    return `${Math.floor(s / 3600)}h ago`;
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-[#E5E7EB] shadow-sm px-5 py-3.5 animate-pulse">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="h-4 bg-slate-100 rounded w-28" />
          <div className="h-4 bg-slate-100 rounded w-px" />
          {[1, 2, 3, 4, 5, 6].map(i => <div key={i} className="h-4 bg-slate-100 rounded w-20" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-[#E5E7EB] shadow-sm px-5 py-3.5">
      <div className="flex items-center gap-4 flex-wrap">
        {/* System status */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className={`w-2 h-2 rounded-full ${statusDot} ${!systemCritical && !systemWarning ? 'animate-pulse' : ''}`} />
          <span className={`text-xs font-semibold ${statusColor}`}>{statusLabel}</span>
        </div>

        <Divider />

        <Pill icon="Globe" label="WordPress" value={`${fmt(wpStats?.sites)} Sites`} color="#21759B" />
        <Pill icon="Image" label="Pinterest" value={`${fmt(pinStats?.totalPins)} Pins`} color="#E60023" />
        <Pill icon="Users" label="Facebook" value={`${fmt(fbStats?.pages)} Pages`} color="#1877F2" />
        <Pill icon="ShoppingBag" label="Etsy" value={`${fmt(etsyStats?.shops)} Shop${etsyStats?.shops !== 1 ? 's' : ''}`} color="#F56400" />

        <Divider />

        <Pill icon="Clock" label="Queue" value={`${fmt(pending)} Pending`} color="#64748B" />
        <Pill icon="Zap" label="Running" value={`${fmt(activeJobs)} Jobs`} color="#7C3AED" />

        {failed > 0 && (
          <>
            <Divider />
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <Icon name="AlertCircle" size={13} className="text-red-500" />
              <span className="text-xs font-semibold text-red-600">{failed} Failed</span>
            </div>
          </>
        )}

        <div className="ml-auto flex-shrink-0 text-xs text-[#94A3B8]">
          Updated {relTime(lastRefresh)}
        </div>
      </div>
    </div>
  );
};

export default AutomationOverview;
